import React from "react";
import { Box, Tooltip } from "@mui/material";
import {
  getPodPrintingTypeBadgeStyle,
  type PodBadgeStyle,
} from "../../../../../models/PodOrder/PodPrintingType";
import { getPodOrderProgressStageBadgeStyle } from "../../../../../models/PodOrder/PodOrderProgressStage";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

type BadgeVariant = "pill" | "tag";

const PodCellBadge = ({
  style,
  variant = "pill",
  label,
}: {
  style: PodBadgeStyle;
  variant?: BadgeVariant;
  label?: string;
}) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
      px: variant === "pill" ? 1.25 : 1,
      py: variant === "pill" ? 0.5 : 0.35,
      borderRadius: variant === "pill" ? "999px" : "6px",
      fontSize: variant === "pill" ? "0.75rem" : "0.72rem",
      lineHeight: variant === "pill" ? 1.2 : undefined,
      fontWeight: 600,
      whiteSpace: "nowrap",
      color: style.color,
      backgroundColor: style.backgroundColor,
    }}
  >
    {label ?? style.label}
  </Box>
);

export const PodOrderPrintingTypeBadge: React.FC<{
  name: string | null;
  variant?: BadgeVariant;
}> = ({ name, variant = "tag" }) => (
  <PodCellBadge
    style={getPodPrintingTypeBadgeStyle(name)}
    variant="tag"
    label={name ?? undefined}
  />
);

export const PodOrderProgressStageBadge: React.FC<{ status: string }> = ({ status }) => (
  <PodCellBadge style={getPodOrderProgressStageBadgeStyle(status)} variant="pill" />
);

const PREVIOUSLY_ALLOCATED_BADGE_STYLE: PodBadgeStyle = {
  label: "Previously Allocated",
  color: "#92400E",
  backgroundColor: "#FDE68A",
};

/** Amber badge for Unassigned rows that were allocated before and then un-assigned. */
export const PodOrderPreviouslyAllocatedBadge: React.FC<{
  unassignedAt?: string | null;
  unassignedByName?: string | null;
}> = ({ unassignedAt, unassignedByName }) => {
  const when = unassignedAt ? formatDate(unassignedAt) : "unknown date";
  const who = unassignedByName?.trim() || "unknown admin";
  return (
    <Tooltip title={`Unassigned on ${when} by ${who}`} placement="top" arrow>
      <Box sx={{ display: "inline-block", maxWidth: "100%", mt: 0.5 }}>
        <PodCellBadge style={PREVIOUSLY_ALLOCATED_BADGE_STYLE} variant="pill" />
      </Box>
    </Tooltip>
  );
};
